// SPDX-License-Identifier: AGPL-3.0-or-later

import styles from '@app/features/accessibility/components/GameModeLaunchToast.module.css';
import GameMode from '@app/features/accessibility/state/GameMode';
import {Button} from '@app/features/ui/button/Button';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {when} from 'mobx';
import type React from 'react';

export const GAME_MODE_LAUNCH_TOAST_TIMEOUT_MS = 30_000;

const MESSAGE_DESCRIPTOR = msg({
	message: "Performance mode is on: others' cameras are off and animations are paused.",
	comment:
		'Toast shown once when the app opens and Performance mode (keeps the app light while you play) is still on from the last time. Short.',
});
const TURN_OFF_DESCRIPTOR = msg({
	message: 'Turn off',
	comment: 'Button in the toast about Performance mode left on: turns Performance mode off.',
});
const KEEP_ON_DESCRIPTOR = msg({
	message: 'Keep it on',
	comment:
		'Button in the toast about Performance mode left on: keeps it on and stops asking on this device until it is turned off.',
});

const stopToastKey = (event: React.KeyboardEvent) => event.stopPropagation();

function GameModeLaunchToastContent({watchHold}: {watchHold: (element: HTMLElement) => () => void}) {
	const {i18n} = useLingui();
	return (
		<span ref={watchHold} className={styles.content} data-flx="accessibility.game-mode-launch-toast.content">
			<span>{i18n._(MESSAGE_DESCRIPTOR)}</span>
			<span className={styles.actions} data-flx="accessibility.game-mode-launch-toast.actions">
				<Button
					small
					fitContent
					onClick={() => GameMode.setLightMode(false)}
					onKeyDown={stopToastKey}
					data-flx="accessibility.game-mode-launch-toast.turn-off"
				>
					{i18n._(TURN_OFF_DESCRIPTOR)}
				</Button>
				<Button
					small
					fitContent
					variant="secondary"
					onClick={GameMode.keepLightModeOn}
					onKeyDown={stopToastKey}
					data-flx="accessibility.game-mode-launch-toast.keep-on"
				>
					{i18n._(KEEP_ON_DESCRIPTOR)}
				</Button>
			</span>
		</span>
	);
}

function showGameModeLaunchToast(): void {
	let id = '';
	let timedOut = false;
	let pointerInside = false;
	let content: HTMLElement | null = null;
	const closeIfFree = () => {
		if (!timedOut || pointerInside || content?.contains(document.activeElement)) return;
		ToastCommands.destroyToast(id);
	};
	const watchHold = (element: HTMLElement) => {
		const enter = () => {
			pointerInside = true;
		};
		const leave = () => {
			pointerInside = false;
			closeIfFree();
		};
		const focusOut = () => setTimeout(closeIfFree, 0);
		content = element;
		element.addEventListener('pointerenter', enter);
		element.addEventListener('pointerleave', leave);
		element.addEventListener('focusout', focusOut);
		return () => {
			content = null;
			element.removeEventListener('pointerenter', enter);
			element.removeEventListener('pointerleave', leave);
			element.removeEventListener('focusout', focusOut);
		};
	};
	id = ToastCommands.createToast({
		type: 'info',
		children: <GameModeLaunchToastContent watchHold={watchHold} />,
		timeout: GAME_MODE_LAUNCH_TOAST_TIMEOUT_MS,
		onTimeout: () => {
			timedOut = true;
			closeIfFree();
		},
	});
}

export function watchGameModeLaunchReminder(): () => void {
	return when(
		() => GameMode.launchReminderDue,
		() => {
			GameMode.markLaunchReminderShown();
			showGameModeLaunchToast();
		},
	);
}
